import React from 'react';
import { formatDollars, formatPercent, formatSI } from '../../../../modules/format';
import styles from './NCRs.module.css';
import { WEIGHT_OPTIONS } from '../../../../slices/quality-details-ncrs/qualityDetailsNcrsConfig';
import { Tooltip } from '@/components/ui/tooltip';
import { Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

const Summary = props => {
  const { ncrsData, categoryColors, packerPlantsInPacker, market } = props;
  const data = ncrsData.summary;

  return (
    <div style={{ marginTop: 20 }}>
      <Text className={styles.QualityNcrs_incidentsPercent}>
        Summary of the <span>last 12 months</span> {packerPlantsInPacker ? 'by establishment' : 'by category'}
      </Text>

      <SummaryBlock
        data={data.incidents}
        packerPlantsInPacker={packerPlantsInPacker}
        categoryColors={categoryColors}
        title="count of incidents"
        label={
          <span>
            <strong>{data.incidents.count}</strong> incidents
          </span>
        }
      />

      <SummaryBlock
        data={data.volume}
        packerPlantsInPacker={packerPlantsInPacker}
        categoryColors={categoryColors}
        title="volume supplied"
        label={
          <span>
            <strong>{formatSI(2)(data?.volume?.count)}</strong> {WEIGHT_OPTIONS[market.key]} (
            <strong>{formatPercent(2)(data?.volume?.percentageOfTotal)}</strong> total volume)
          </span>
        }
      />
      <SummaryBlock
        data={data.dollars}
        packerPlantsInPacker={packerPlantsInPacker}
        categoryColors={categoryColors}
        title="claim amount"
        label={
          <span>
            <strong>{formatDollars(2)(data?.dollars?.count)}</strong> in claim dollars
          </span>
        }
      />
    </div>
  );
};

Summary.propTypes = {
  categoryColors: PropTypes.objectOf(PropTypes.string),
  packerPlantsInPacker: PropTypes.arrayOf(
    PropTypes.shape({
      packerPlantId: PropTypes.string,
      color: PropTypes.string,
    })
  ),
  summary: PropTypes.shape({
    incidents: PropTypes.shape({
      count: PropTypes.number,
      byPackerPlant: PropTypes.arrayOf(
        PropTypes.shape({
          packerPlantId: PropTypes.string,
          count: PropTypes.number,
          packerPlantName: PropTypes.string,
        })
      ),
      byCategory: PropTypes.arrayOf(
        PropTypes.shape({
          category: PropTypes.string,
          count: PropTypes.number,
        })
      ),
    }),
    volume: PropTypes.shape({
      count: PropTypes.number,
      percentageOfTotal: PropTypes.number,
    }),
    dollars: PropTypes.shape({
      count: PropTypes.number,
    }),
  }),
  market: PropTypes.shape({
    key: PropTypes.string.isRequired,
  }).isRequired,
  ncrsData: PropTypes.shape({
    summary: PropTypes.shape({
      incidents: PropTypes.shape({
        count: PropTypes.number,
        byPackerPlant: PropTypes.arrayOf(
          PropTypes.shape({
            packerPlantId: PropTypes.string,
            count: PropTypes.number,
            packerPlantName: PropTypes.string,
          })
        ),
        byCategory: PropTypes.arrayOf(
          PropTypes.shape({
            category: PropTypes.string,
            count: PropTypes.number,
          })
        ),
      }),
      volume: PropTypes.shape({
        count: PropTypes.number,
        percentageOfTotal: PropTypes.number,
      }),
      dollars: PropTypes.shape({
        count: PropTypes.number,
      }),
    }),
  }),
};

const SummaryBlock = props => {
  const { data, title, label, packerPlantsInPacker, categoryColors } = props;

  return (
    <div className={styles.QualityNcrs_summaryBlock}>
      <div className={styles.QualityNcrs_summaryTitle}>
        {/* <a> */}
        <Text className={styles.QualityNcrs_label}>{title}</Text>
        {/* <Icon style={{ position: 'relative', width: 14 }} component={ChevronRight} /> */}
        {/* </a> */}
      </div>
      <div
        style={{
          display: 'flex',
          width: `100%`,
        }}
      >
        {packerPlantsInPacker && data.byPackerPlant.length > 0
          ? data.byPackerPlant
              .slice()
              .sort((a, b) => b.count - a.count)
              .map(p => (
                <Tooltip key={`summary-${p.packerPlantId}`} content={`${p.packerPlantName} - ${p.count}`}>
                  <div
                    style={{
                      width: `${data.count === 0 ? 0 : (p.count * 100) / data.count}%`,
                      height: 10,
                      backgroundColor: packerPlantsInPacker.find(pac => pac.packerPlantId === p.packerPlantId)?.color,
                      backgroundImage:
                        'repeating-linear-gradient(-45deg, transparent, transparent 2px, rgba(255,255,255,.5) 2px, rgba(255,255,255,.5) 4px)',
                      marginRight: 2,
                    }}
                  />
                </Tooltip>
              ))
          : data?.byCategory &&
            data.byCategory
              .slice()
              .sort((a, b) => b.count - a.count)
              .map(c => (
                <Tooltip key={`summary-${c.category}`} content={`${c.category} - ${Math.round(c.count)}`}>
                  <div
                    style={{
                      width: `${data.count === 0 ? 0 : (c.count * 100) / data.count}%`,
                      height: 10,
                      backgroundColor: categoryColors[c.category],
                      marginRight: 2,
                    }}
                  />
                </Tooltip>
              ))}
      </div>
      <p style={{ marginTop: 6 }}>{label}</p>
    </div>
  );
};

export default Summary;

SummaryBlock.propTypes = {
  categoryColors: PropTypes.objectOf(PropTypes.string),
  data: PropTypes.shape({
    count: PropTypes.number,
    byPackerPlant: PropTypes.arrayOf(
      PropTypes.shape({
        packerPlantId: PropTypes.string,
        count: PropTypes.number,
        packerPlantName: PropTypes.string,
      })
    ),
    byCategory: PropTypes.arrayOf(
      PropTypes.shape({
        category: PropTypes.string,
        count: PropTypes.number,
      })
    ),
  }),
  packerPlantsInPacker: PropTypes.arrayOf(
    PropTypes.shape({
      packerPlantId: PropTypes.string,
      color: PropTypes.string,
    })
  ),
  title: PropTypes.string,
  label: PropTypes.node,
};
